import { useCallback, useRef, type MouseEvent, type RefObject } from 'react';

import type { ChartFinder, EChartsBaseHandle } from './EChartsBase';

type UseChartCursorDataYOptions = {
  chartRef: RefObject<EChartsBaseHandle | null>;
  // Which coordinate system to convert against. Charts whose series[0] is not a plotted line
  // (e.g. a marker scatter series) must pass a grid finder rather than { seriesIndex: 0 }.
  finder: ChartFinder;
  // Only convert when the chart actually consumes the cursor value — otherwise this would add a
  // convertFromPixel to every mousemove on charts that don't use nearest-series tooltips.
  enabled?: boolean;
  // Run after this hook's own bookkeeping, so callers can chain their existing handlers.
  onMouseMoveCapture?: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: () => void;
};

/**
 * Tracks the cursor's position in *data* space along the y axis, for tooltip formatters that pick
 * the series nearest the pointer (see utils/chart-options/shared/nearestSeries.ts).
 *
 * The handlers must be attached in the capture phase on an element wrapping the chart, so the
 * value is up to date before ZRender's own (bubble-phase) handler invokes the tooltip formatter.
 * The value is kept in a ref rather than state: this runs on every mousemove and must not
 * re-render the chart or rebuild its option.
 */
export default function useChartCursorDataY({
  chartRef,
  finder,
  enabled = true,
  onMouseMoveCapture,
  onMouseLeave,
}: UseChartCursorDataYOptions): {
  getCursorDataY: () => number | null;
  onMouseMoveCapture: (event: MouseEvent<HTMLDivElement>) => void;
  onMouseLeave: () => void;
} {
  const cursorDataYRef = useRef<number | null>(null);
  const getCursorDataY = useCallback(() => cursorDataYRef.current, []);

  const handleMouseMoveCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (enabled) {
        const chart = chartRef.current;
        const chartDom = chart?.getDom();
        if (chart && chartDom) {
          const rect = chartDom.getBoundingClientRect();
          // Returns null while the chart is initializing or re-initializing rather than throwing,
          // so the cursor simply has no data position for that frame.
          const coords = chart.convertFromPixel(finder, [event.clientX - rect.left, event.clientY - rect.top]);
          cursorDataYRef.current = coords ? (coords[1] ?? null) : null;
        } else {
          // Mid-(re)initialization there is no coordinate system to convert against. Drop the
          // previous value instead of keeping it: a stale position makes the tooltip pick the
          // series nearest to where the cursor used to be, which is worse than picking none.
          cursorDataYRef.current = null;
        }
      }
      onMouseMoveCapture?.(event);
    },
    [chartRef, finder, enabled, onMouseMoveCapture],
  );

  const handleMouseLeave = useCallback(() => {
    cursorDataYRef.current = null;
    onMouseLeave?.();
  }, [onMouseLeave]);

  return { getCursorDataY, onMouseMoveCapture: handleMouseMoveCapture, onMouseLeave: handleMouseLeave };
}
